/**
 * Interactive Site Plan Page
 * Displays the interactive map with unit selection
 * Supports URL state persistence for shareable links
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { NavBar } from '@/components/NavBar';
import { Footer } from '@/components/Footer';
import { InteractivePropertyView, type ViewMode } from '@/components/InteractivePropertyView';
import { UnitDetailModal } from '@/components/UnitDetailModal';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import { StickyMobileCTA } from '@/components/StickyMobileCTA';
import { LeadForm } from '@/components/LeadForm';
import { MeetingModal } from '@/components/MeetingModal';
import { trackEvent } from '@/lib/analytics';
import { getUnitByNumber } from '@/lib/units';
import type { PropertyUnit } from '@/lib/units';

// Valid filter values for type safety
const VALID_VIEWS: ViewMode[] = ['site-plan', 'building-facade'];
const VALID_BLOCKS = ['A', 'B'] as const;
const VALID_VIEW_FILTERS = ['all', 'lagoon', 'canal'];
const VALID_AVAILABILITY_FILTERS = ['all', 'available', 'reserved', 'sold'];
const VALID_CATEGORY_FILTERS = ['all', 'townhouse', '2bed', '1bed'];
const VALID_FLOOR_FILTERS = ['all', 'GF', 'second', 'third', 'fourth'];

export const SitePlan = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialLoadRef = useRef(true);
  
  // Read initial values from URL (with validation)
  const getValidParam = <T extends string>(
    param: string | null, 
    validValues: readonly T[] | T[], 
    defaultValue: T
  ): T => {
    if (param && (validValues as readonly string[]).includes(param)) {
      return param as T;
    }
    return defaultValue;
  };

  // URL state
  const view = getValidParam(searchParams.get('view'), VALID_VIEWS, 'site-plan');
  const block = getValidParam(searchParams.get('block'), VALID_BLOCKS, 'A');
  const unitParam = searchParams.get('unit');
  const viewFilter = getValidParam(searchParams.get('viewType'), VALID_VIEW_FILTERS, 'all');
  const availabilityFilter = getValidParam(searchParams.get('availability'), VALID_AVAILABILITY_FILTERS, 'all');
  const categoryFilter = getValidParam(searchParams.get('category'), VALID_CATEGORY_FILTERS, 'all');
  const floorFilter = getValidParam(searchParams.get('floor'), VALID_FLOOR_FILTERS, 'all');
  const showUnits = searchParams.get('showUnits') === 'true';

  // Local state for modals
  const [selectedUnit, setSelectedUnit] = useState<string | null>(unitParam);
  const [unitDetail, setUnitDetail] = useState<PropertyUnit | null>(null);
  const [unitDetailOpen, setUnitDetailOpen] = useState(false);
  const [leadFormOpen, setLeadFormOpen] = useState(false);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [loadingUnit, setLoadingUnit] = useState(false);

  // Helper to update URL params (only sets non-default values)
  const updateParams = useCallback((updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      // Don't include default values in URL for cleaner URLs
      const isDefault = 
        (key === 'view' && value === 'site-plan') ||
        (key === 'block' && value === 'A') ||
        (key === 'viewType' && value === 'all') ||
        (key === 'availability' && value === 'all') ||
        (key === 'category' && value === 'all') ||
        (key === 'floor' && value === 'all') ||
        (key === 'showUnits' && value === 'false');
      
      if (value && !isDefault) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Load unit from URL on initial mount
  useEffect(() => {
    if (initialLoadRef.current && unitParam) {
      initialLoadRef.current = false;
      handleUnitClick(unitParam);
    }
  }, [unitParam]);

  const handleUnitClick = async (unitNumber: string) => {
    trackEvent('unit_map_click', { unit_number: unitNumber });
    setSelectedUnit(unitNumber);
    updateParams({ unit: unitNumber });
    setLoadingUnit(true);
    
    try {
      const { data, error } = await getUnitByNumber(unitNumber);
      if (error) {
        console.error('Error loading unit:', error);
      } else if (data) {
        setUnitDetail(data);
        setUnitDetailOpen(true);
      }
    } catch (error) {
      console.error('Error loading unit:', error);
    } finally {
      setLoadingUnit(false);
    }
  };

  // View change handler
  const handleViewChange = (newView: ViewMode) => {
    updateParams({ view: newView });
  };

  // Block change handler
  const handleBlockChange = (newBlock: 'A' | 'B') => {
    updateParams({ block: newBlock });
  };

  // Filter change handlers
  const handleViewFilterChange = (filter: string) => {
    updateParams({ viewType: filter });
  };

  const handleAvailabilityFilterChange = (filter: string) => {
    updateParams({ availability: filter });
  };

  const handleCategoryFilterChange = (filter: string) => {
    updateParams({ category: filter });
  };

  const handleFloorFilterChange = (filter: string) => {
    updateParams({ floor: filter });
  };

  // Show units toggle handler
  const handleShowUnitsChange = (show: boolean) => {
    updateParams({ showUnits: show ? 'true' : 'false' });
  };

  // Keep unit selected when modal closes (unit stays highlighted on map)
  const handleUnitDetailClose = (open: boolean) => {
    setUnitDetailOpen(open);
    // Don't clear selectedUnit - keep it highlighted on the map
  };

  // Reset all filters and selection to defaults
  const handleReset = () => {
    setSelectedUnit(null);
    setSearchParams(new URLSearchParams(), { replace: true });
    trackEvent('site_plan_reset');
  };

  const handleWhatsAppClick = () => {
    const whatsappNumber = '13451234567';
    const message = selectedUnit 
      ? encodeURIComponent(`Hi! I'm interested in unit ${selectedUnit} at The Pearl at Grand Harbour.`)
      : encodeURIComponent("Hi! I'm interested in learning more about The Pearl at Grand Harbour properties.");
    
    window.open(`https://wa.me/${whatsappNumber}?text=${message}`, '_blank');
    trackEvent('whatsapp_click', { location: 'site_plan', unit: selectedUnit });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <NavBar 
        onRegisterClick={() => setLeadFormOpen(true)} 
        onConsultClick={() => setMeetingOpen(true)}
      />

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-deep-navy via-midnight-blue to-deep-navy">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white max-w-4xl mx-auto">
            <h1 className="font-display text-4xl lg:text-6xl font-bold mb-6">
              Interactive Map
            </h1>
            <p className="text-xl lg:text-2xl text-white/90 mb-8 max-w-2xl mx-auto">
              Explore the residences at The Pearl. Use the filters to compare Lagoon and Canal locations, then select any unit to view details.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Map Section */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <InteractivePropertyView
            onUnitClick={handleUnitClick}
            selectedUnit={selectedUnit}
            // Controlled view/block
            view={view}
            onViewChange={handleViewChange}
            block={block}
            onBlockChange={handleBlockChange}
            // Controlled filters
            viewFilter={viewFilter}
            availabilityFilter={availabilityFilter}
            categoryFilter={categoryFilter}
            floorFilter={floorFilter}
            onViewFilterChange={handleViewFilterChange}
            onAvailabilityFilterChange={handleAvailabilityFilterChange}
            onCategoryFilterChange={handleCategoryFilterChange}
            onFloorFilterChange={handleFloorFilterChange}
            // Show units toggle
            showUnits={showUnits}
            onShowUnitsChange={handleShowUnitsChange}
            // Reset
            onReset={handleReset}
          />
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-soft-cream">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-display text-3xl font-bold text-foreground mb-6 text-center">
              Lagoon & Canal Living
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-semibold text-lg mb-3">Canal View</h3>
                <p className="text-muted-foreground">
                  The Canal Residence sits along wide, peaceful waterways, offering privacy and a gentle stillness.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-3">Lagoon View</h3>
                <p className="text-muted-foreground">
                  The Lagoon Residence places you at the edge of a 20,000 SF lagoon, where soft shorelines, calm waters, and a relaxed, beach-like energy become part of everyday life.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      {/* Persistent Conversion UI */}
      <StickyMobileCTA onClick={() => setLeadFormOpen(true)} />
      <WhatsAppButton onClick={handleWhatsAppClick} />

      {/* Modals */}
      <UnitDetailModal
        open={unitDetailOpen}
        onOpenChange={handleUnitDetailClose}
        unit={unitDetail}
        onRegisterClick={() => {
          setUnitDetailOpen(false);
          setLeadFormOpen(true);
        }}
        onConsultClick={() => {
          setUnitDetailOpen(false);
          setMeetingOpen(true);
        }}
      />
      <LeadForm 
        open={leadFormOpen} 
        onOpenChange={setLeadFormOpen}
        unitNumber={unitDetail?.unit_number}
      />
      <MeetingModal
        open={meetingOpen}
        onOpenChange={setMeetingOpen}
        onWhatsAppClick={handleWhatsAppClick}
        unitNumber={unitDetail?.unit_number}
      />
    </div>
  );
};
